import { REPORT_GROUP, type IReportListItem, type IReportsResponse } from '@/types/shared-types'
import type { IUiTableColumn } from '@/components/UiTable/UiTable.types'
import { formatDate as formatDateValue } from '@/utils/date'
import { useRouteLink } from '@/composables/useRouteLink'
import { ROUTE_PATHS } from '@/utils/route'
import { useRuntimeConfig } from '#imports'
import type {
  IDataViewCategoryScope,
  IDataViewListParams,
  IDataViewListOptions,
  IDataViewStateOptions,
} from '@/domains/investor/components/DataView/DataView.types'
import { computed, watch } from 'vue'
import { CATEGORY_ALL, CATEGORY_BY_SLUG, type TInvestorCategory } from '@/domains/investor/const/InvestorCategories.const'
import type { IDataViewFilters } from '@/domains/investor/components/DataView/DataViewFilters/DataViewFilters.types'
import { queryParam, useQueryState } from '@/domains/investor/composables/useQueryState'

export const useDataView = () => {
  const resolveCategoryScope = (
    defaultCategoryScope: IDataViewCategoryScope,
    categoryScope?: IDataViewCategoryScope,
  ): IDataViewCategoryScope => ({
    ...defaultCategoryScope,
    ...categoryScope,
    allSecondCategories: categoryScope?.allSecondCategories
      ?? categoryScope?.allowedSecondCategories
      ?? defaultCategoryScope.allowedSecondCategories,
  })

  const formatDate = (date: string) => ({
    date: formatDateValue(date),
    hour: formatDateValue(date, 'HH:mm'),
  })

  const fetchList = ({ locale, page, perPage, signal, ...filters }: IDataViewListParams) => {
    return $fetch<IReportsResponse>('/api/v1/contentful/investor/reports', {
      signal,
      query: {
        locale,
        group: REPORT_GROUP,
        ...filters,
        page,
        perPage,
      },
    })
  }

  const useState = ({
    defaultCategoryScope,
    options = {},
    useFilters,
  }: IDataViewStateOptions) => {
    const categoryScope = resolveCategoryScope(defaultCategoryScope, options.categoryScope)

    const { category: secondCategory, search, year, months, page } = useQueryState({
      category: queryParam.oneOf<TInvestorCategory>(CATEGORY_BY_SLUG, CATEGORY_ALL),
      search: queryParam.string(),
      year: queryParam.numberList(),
      months: queryParam.numberList(),
      page: queryParam.page(),
    })

    const filters = computed<IDataViewFilters>(() => ({
      mainCategory: categoryScope.mainCategories,
      secondCategory: secondCategory.value === CATEGORY_ALL
        ? categoryScope.allSecondCategories
        : secondCategory.value,
      search: search.value,
      year: year.value,
      months: months.value,
    }))

    const {
      searchDraft,
      filterOptions,
      hasActiveFilters,
    } = useFilters(secondCategory, year, months, {
      categoryScope,
      enableSearch: options.enableSearch,
    })

    const clearFilters = () => {
      secondCategory.value = CATEGORY_ALL
      searchDraft.value = ''
      search.value = undefined
      year.value = []
      months.value = []
    }

    const onSearch = (value: string) => {
      search.value = value || undefined
    }

    watch(search, (value) => {
      searchDraft.value = value ?? ''
    }, { immediate: true })

    watch(filters, () => {
      page.value = 1
    })

    return {
      secondCategory,
      search,
      searchDraft,
      year,
      months,
      page,
      filters,
      filterOptions,
      hasActiveFilters,
      clearFilters,
      onSearch,
      categoryScope,
    }
  }

  const useList = async ({
    cacheKey,
    filters,
    page,
    perPage,
    baseKey,
    linkTextKey,
    fetchList: customFetchList,
  }: IDataViewListOptions) => {
    const { locale } = useI18n()
    const { cacheDisabled } = useRuntimeConfig().app
    const { t } = useI18n()
    const listFetcher = customFetchList ?? fetchList

    const tableColumns = computed<IUiTableColumn<Record<string, unknown>>[]>(() => [
      {
        key: t(`${baseKey}.table.columns[0].key`),
        title: t(`${baseKey}.table.columns[0].title`),
      },
      {
        key: t(`${baseKey}.table.columns[1].key`),
        title: t(`${baseKey}.table.columns[1].title`),
      },
    ])

    const { routeLink } = useRouteLink()

    const formatLink = (url: string) => ({
      text: t(linkTextKey),
      url: url ? routeLink(ROUTE_PATHS.FOR_INVESTORS_ARTICLE_URL, { url }) : '',
    })

    const adaptItems = (items: IReportListItem[]) => {
      return items.map(item => ({
        ...item,
        formattedDate: {
          ...formatDate(item.date),
        },
        formattedLink: {
          ...formatLink(item.url),
        },
      }))
    }

    const { data, status } = await useAsyncData<IReportsResponse>(
      cacheKey,
      (_nuxtApp, { signal }) => listFetcher({
        ...toValue(filters),
        locale: locale.value,
        page: toValue(page),
        perPage,
        signal,
      }),
      {
        watch: [locale, () => toValue(filters), () => toValue(page)],
        server: cacheDisabled,
      },
    )

    return {
      items: computed(() => adaptItems(data.value?.reports ?? [])),
      columns: tableColumns,
      total: computed(() => data.value?.total ?? 0),
      isLoading: computed(() => status.value === 'pending'),
    }
  }

  return {
    formatDate,
    fetchList,
    useState,
    useList,
  }
}
